package vegasful.admin.views

import androidx.compose.runtime.*
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Application
import vegasful.admin.api.EventSearch
import vegasful.admin.api.SocialImageDateStyle
import vegasful.admin.api.client.Event
import vegasful.admin.api.client.query
import vegasful.admin.components.box
import vegasful.admin.components.boxMessage
import vegasful.admin.components.boxSpinner
import vegasful.admin.components.button
import vegasful.admin.components.entityView
import vegasful.admin.components.table
import kotlin.time.Duration.Companion.hours

/**
 * Today page — events scheduled for the next 24 hours, with checkboxes to
 * select a batch for social-image generation.
 *
 * Uses the public (non-admin) events.search API to load the window.
 */
@Composable
fun today() {
    var events by remember { mutableStateOf<List<Event>?>(null) }
    val selected = remember { mutableStateMapOf<String, Boolean>() }
    // Context-aware default: events on this page are by definition today, so
    // start with dateStyle=TODAY instead of the schema default DEFAULT.
    var options by remember {
        mutableStateOf(SlideOptions(dateStyle = SocialImageDateStyle.TODAY))
    }

    LaunchedEffect("today") {
        val now = Clock.System.now()
        val end = now + 24.hours

        events = Application.api.query {
            events {
                search(EventSearch(start = now.toString(), end = end.toString(), pageSize = 200)) {
                    events {
                        id
                        name
                        path
                        date
                        time
                        ts
                        venue {
                            id
                            name
                        }
                    }
                }
            }
        }.events.search.events.sortedWith(
            // ts is null for an event announced for a date whose start time has not been given
            // yet, so date and time are the sort, with a missing time last within its day rather
            // than compared as the string "null".
            compareBy({ it.date ?: "" }, { it.time ?: "~" })
        )
    }

    contentContainer {
        entityView {
            title = "Today"
            subTitle = "Events in the next 24 hours"

            content {
                box({
                    title = "Events"
                    paddedContent = false

                    action {
                        val count = selected.count { it.value }
                        this.title = if (count > 0) "Generate Social Images ($count)" else "Generate Social Images"
                        this.primary = true
                        this.enabled = count > 0
                        this.action {
                            val ids = selected.filter { it.value }.keys.toList()
                            if (ids.isNotEmpty()) {
                                val params = listOf(
                                    "ids=" + ids.joinToString(","),
                                    "renderer=${options.renderer.name}",
                                    "dateStyle=${options.dateStyle.name}",
                                    "omitTime=${options.omitTime}",
                                    "includeTagline=${options.includeTagline}",
                                    "includeVenue=${options.includeVenue}",
                                    "forceCover=${options.forceCover}",
                                ).joinToString("&")
                                Application.navigation.navigate("content/today/batch?$params")
                            }
                        }
                    }
                }) {
                    val current = events
                    when {
                        current == null -> boxSpinner("Loading events…")
                        current.isEmpty() -> boxMessage("No events scheduled in the next 24 hours.")
                        else -> {
                            Div({ style { padding(0.px, 16.px) } }) {
                                slideOptionsForm(options) { options = it }
                            }
                            todaySelectionBar(current, selected)
                            table<Event> {
                                items(current)

                                column {
                                    width = 30.px
                                    content { event ->
                                        Input(InputType.Checkbox) {
                                            checked(selected[event.id] == true)
                                            onInput {
                                                selected[event.id] = it.value
                                            }
                                        }
                                    }
                                }

                                column("Time") {
                                    width = 90.px
                                    content { event ->
                                        Text(formatEventTime(event.time))
                                    }
                                }

                                column("Event") {
                                    content { event ->
                                        A(href = "#content/events/${event.id}") {
                                            Text(event.name)
                                        }
                                    }
                                }

                                column("Venue") {
                                    content { event ->
                                        event.venue?.let { venue ->
                                            Text(venue.name)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun todaySelectionBar(events: List<Event>, selected: MutableMap<String, Boolean>) {
    Div({
        style {
            padding(8.px, 16.px)
            display(DisplayStyle.Flex)
            property("gap", "12px")
            alignItems(AlignItems.Center)
            fontSize((0.85).cssRem)
        }
    }) {
        A(href = "#", attrs = {
            onClick {
                it.preventDefault()
                events.forEach { selected[it.id] = true }
            }
        }) { Text("Select all") }

        A(href = "#", attrs = {
            onClick {
                it.preventDefault()
                events.forEach { selected[it.id] = false }
            }
        }) { Text("Select none") }
    }
}

/**
 * Trim an AWSTime like "20:00:00" to "20:00".
 *
 * A null time is an event announced for a date whose start time has not been given yet. Named
 * rather than left blank, so that an editor scanning the day can see which rows are still waiting
 * on a time instead of reading the gap as a rendering fault.
 */
private fun formatEventTime(time: String?): String {
    if (time == null) return "TBA"
    val parts = time.split(":")
    return if (parts.size >= 2) "${parts[0]}:${parts[1]}" else time
}
